import { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { type School, type SchoolInput, type HamburgLookupResult, useHamburgSearch, useSchulen } from './api/schulenApi'
import { useFirma } from '../hr/api/firmaApi'
import { usePermissions } from '../../auth/usePermissions'
import { SchoolTourPanel } from './SchoolTourPanel'
import { notify } from '../../store/notificationStore'
import { Button, Badge, Dropdown } from '../../shared/ui'
import SchoolIdentifiersPanel from './identifiers/SchoolIdentifiersPanel'
import IdentifierEditDialog from './identifiers/IdentifierEditDialog'
import { useIdentifiers } from './identifiers/identifiersApi'
import type { SchoolSupplierIdentifier } from './identifiers/types'
import SchoolCardsPanel from './karten/SchoolCardsPanel'
import { SCHULTYPEN, schultypAnzeige, schultypVon } from './schultyp'

type ModalMode = 'view' | 'edit' | 'add'

interface Props {
  school?: School | null
  initialMode?: ModalMode
  onClose: () => void
  onSave: (data: SchoolInput) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  isSaving?: boolean
  isDeleting?: boolean
}

const EMPTY: SchoolInput = {
  name: '', schulnummer: null, firma: null, bundesland: null, stadt: null,
  plz: null, adresse: null, lat: null, lon: null, projektleiter: null,
  kinderkategorie: null, abkuerzung: null, maxStufe: null, mandantId: null,
  schuleLoginName: null, status: 'Aktiv',
}

// M27 T10b: the school type is mandatory, exactly these four values (owner b_q16 / k_schultyp).
const SCHULTYP_OPTIONEN = SCHULTYPEN.map(s => ({ value: s.value, label: schultypAnzeige(s.value) }))

// ── Sub-components ────────────────────────────────────────────────────────────

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-on-surface">{value || <span className="text-on-surface-variant/50 font-normal">—</span>}</p>
    </div>
  )
}

function Input({ label, value, onChange, placeholder, colSpan, readOnly, required, invalid }: {
  label: string; value: string; onChange?: (v: string) => void;
  placeholder?: string; colSpan?: number; readOnly?: boolean;
  required?: boolean; invalid?: boolean
}) {
  return (
    <div className={colSpan === 2 ? 'col-span-2' : colSpan === 3 ? 'col-span-3' : ''}>
      <label className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-on-surface-variant mb-1.5">
        {label}
        {required && <span className="text-error" aria-hidden="true">*</span>}
      </label>
      <input
        readOnly={readOnly}
        aria-required={required}
        aria-invalid={invalid || undefined}
        className={`w-full border p-3 rounded-xl text-sm focus:outline-none transition-colors ${
          readOnly
            ? 'bg-surface-container border-outline-variant/40 text-on-surface-variant cursor-default'
            : invalid
              ? 'bg-error-container/40 border-error/40 focus:border-error focus:bg-surface-container-lowest ring-1 ring-error/20'
              : 'bg-surface-container-low border-outline-variant focus:border-primary focus:bg-surface-container-lowest'
        }`}
        value={value} onChange={e => onChange?.(e.target.value)} placeholder={placeholder || label}
      />
    </div>
  )
}

function SchulnummerInput({ value, onChange, invalid, errorText }: {
  value: string | number | null | undefined
  onChange: (v: string) => void
  invalid?: boolean
  errorText?: string
}) {
  // Parse the combined value into { base, standort }. Base = up to 4 digits, Standort = single digit.
  // Coerce to string defensively — the backend occasionally returns
  // schulnummer as a number even though the TS type says string.
  const raw = String(value ?? '').trim()
  const m = raw.match(/^(\d{1,4})(?:-(\d))?$/)
  const base = m ? (m[1] || '') : raw.replace(/\D/g, '').slice(0, 4)
  const standort = m ? (m[2] || '') : ''

  const update = (nextBase: string, nextStandort: string) => {
    const b = nextBase.replace(/\D/g, '').slice(0, 4)
    const s = nextStandort.replace(/\D/g, '').slice(0, 1)
    if (!b && !s) onChange('')
    else if (!s) onChange(b)
    else onChange(`${b}-${s}`)
  }

  const sharedCls = (isInvalid?: boolean) =>
    `bg-surface-container-low border p-3 rounded-xl text-sm focus:outline-none transition-colors font-mono tabular-nums ${
      isInvalid
        ? 'bg-error-container/40 border-error/40 focus:border-error focus:bg-surface-container-lowest ring-1 ring-error/20'
        : 'border-outline-variant focus:border-primary focus:bg-surface-container-lowest'
    }`

  return (
    <div>
      <label className="flex items-baseline gap-3 text-[9px] font-black uppercase tracking-widest text-on-surface-variant mb-1.5">
        <span className="flex-1">Schulnummer</span>
        <span className="w-14 text-right">Standort</span>
      </label>
      <div className="flex items-center gap-2">
        <input
          inputMode="numeric"
          maxLength={4}
          aria-label="Schulnummer"
          aria-invalid={invalid || undefined}
          className={`flex-1 ${sharedCls(invalid)}`}
          value={base}
          onChange={e => update(e.target.value, standort)}
          placeholder="5043"
        />
        <span className="text-on-surface-variant/50 font-black text-lg select-none" aria-hidden="true">–</span>
        <input
          inputMode="numeric"
          maxLength={1}
          aria-label="Standort"
          aria-invalid={invalid || undefined}
          className={`w-14 text-center ${sharedCls(invalid)}`}
          value={standort}
          onChange={e => update(base, e.target.value)}
          placeholder="0"
        />
      </div>
      {errorText ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold text-error">
          <span className="material-symbols-outlined !text-[12px]">error</span>
          {errorText}
        </p>
      ) : base && !standort ? (
        <p className="mt-1.5 text-[10px] font-medium text-on-surface-variant">
          Wird gespeichert als <code className="font-mono font-bold">{base}-0</code> (Hauptstandort)
        </p>
      ) : null}
    </div>
  )
}

function StatusDropdown({ label, value, options, onChange, placeholder = 'Bitte wählen…', variant = 'form' }: {
  label?: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  placeholder?: string
  variant?: 'form' | 'pill-active' | 'pill-inactive'
}) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [])

  const selected = options.find(o => o.value === value)

  const buttonClass = variant === 'form'
    ? `w-full bg-surface-container-low border p-3 rounded-xl text-sm flex items-center justify-between transition-all outline-none ${isOpen ? 'border-primary ring-4 ring-primary/5 bg-surface-container-lowest' : 'border-outline-variant hover:border-primary/20'}`
    : variant === 'pill-active'
      ? `px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-widest border outline-none cursor-pointer transition-all flex items-center gap-1.5 bg-emerald-50 border-emerald-200 text-emerald-700 ${isOpen ? 'ring-4 ring-emerald-200/40' : ''}`
      : `px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-widest border outline-none cursor-pointer transition-all flex items-center gap-1.5 bg-amber-50 border-amber-200 text-amber-600 ${isOpen ? 'ring-4 ring-amber-200/40' : ''}`

  return (
    <div className={variant === 'form' ? 'w-full' : ''} ref={ref}>
      {label && (
        <label className="block text-[9px] font-black uppercase tracking-widest text-on-surface-variant mb-1.5">{label}</label>
      )}
      <div className="relative">
        <button type="button" onClick={() => setIsOpen(v => !v)} className={buttonClass}>
          <span className={selected ? '' : 'text-on-surface-variant font-normal'}>
            {selected?.label || placeholder}
          </span>
          <span className={`material-symbols-outlined transition-transform duration-300 ${variant === 'form' ? '!text-[18px] text-on-surface-variant' : '!text-sm'} ${isOpen ? 'rotate-180' : ''}`}>
            expand_more
          </span>
        </button>

        {isOpen && (
          <div className={`absolute ${variant === 'form' ? 'left-0 right-0' : 'right-0 min-w-[120px]'} top-full mt-1.5 bg-surface-container-lowest border border-outline-variant/40 rounded-xl shadow-2xl overflow-hidden z-[110] animate-[fade-in-up_0.2s_ease-out]`}>
            {options.map(opt => (
              <div key={opt.value}
                onClick={() => { onChange(opt.value); setIsOpen(false) }}
                className={`px-4 py-2.5 text-xs font-medium cursor-pointer transition-colors ${
                  value === opt.value ? 'bg-primary/5 text-primary' : 'text-on-surface hover:bg-surface-container-low'
                }`}
              >
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function SchoolNameAutocomplete({ value, onChange, onSelect, required, invalid, currentSchoolId }: {
  value: string
  onChange: (v: string) => void
  onSelect: (hit: HamburgLookupResult) => void
  required?: boolean
  invalid?: boolean
  currentSchoolId?: string | null   // exclude the school being edited from "already exists" matches
}) {
  const [debounced, setDebounced] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), 300)
    return () => clearTimeout(t)
  }, [value])

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [])

  const { data: results = [], isFetching } = useHamburgSearch(debounced)
  const { data: existingSchools = [] } = useSchulen()

  // Build sets of existing Schulnummern + names (case-insensitive). Coerce any numeric IDs to string.
  const existing = useMemo(() => {
    const byNummer = new Set<string>()
    const byName = new Set<string>()
    for (const s of existingSchools) {
      if (currentSchoolId && s.id === currentSchoolId) continue
      if (s.schulnummer != null) byNummer.add(String(s.schulnummer).trim().toLowerCase())
      if (s.name) byName.add(s.name.trim().toLowerCase())
    }
    return { byNummer, byName }
  }, [existingSchools, currentSchoolId])

  const resultsAnnotated = useMemo(() =>
    results.map(r => {
      const nummer = r.schulnummer != null ? String(r.schulnummer).trim().toLowerCase() : ''
      const name = r.name ? r.name.trim().toLowerCase() : ''
      return {
        hit: r,
        isDuplicate: (nummer && existing.byNummer.has(nummer)) || (name && existing.byName.has(name)),
      }
    }),
    [results, existing],
  )
  const duplicateCount = resultsAnnotated.filter(x => x.isDuplicate).length
  const showPanel = isOpen && debounced.length >= 2

  return (
    <div ref={ref} className="col-span-2 relative">
      <label className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-on-surface-variant mb-1.5">
        <span className="flex items-center gap-1">
          Schulname
          {required && <span className="text-error" aria-hidden="true">*</span>}
        </span>
        <span className="ml-2 text-primary/60 normal-case tracking-normal font-medium">— Hamburg-Portal Autocomplete</span>
      </label>
      <div className="relative">
        <input
          type="text"
          aria-required={required}
          aria-invalid={invalid || undefined}
          className={`w-full p-3 pr-10 rounded-xl text-sm font-bold focus:outline-none transition-colors border ${
            invalid
              ? 'bg-error-container/40 border-error/40 focus:border-error focus:bg-surface-container-lowest ring-1 ring-error/20'
              : 'bg-surface-container-low border-outline-variant focus:border-primary focus:bg-surface-container-lowest'
          }`}
          value={value}
          onChange={e => { onChange(e.target.value); setIsOpen(true) }}
          onFocus={() => setIsOpen(true)}
          placeholder="Suche… oder Name eingeben"
        />
        <span className={`material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 !text-[18px] text-on-surface-variant ${isFetching ? 'animate-spin' : ''}`}>
          {isFetching ? 'progress_activity' : 'travel_explore'}
        </span>
      </div>

      {showPanel && (
        <div className="absolute left-0 right-0 top-full mt-1.5 bg-surface-container-lowest border border-outline-variant/40 rounded-xl shadow-2xl overflow-hidden z-[120] animate-[fade-in-up_0.15s_ease-out] max-h-80 overflow-y-auto">
          {isFetching && results.length === 0 && (
            <div className="px-4 py-3 text-xs text-on-surface-variant">Suche im Hamburg-Portal…</div>
          )}
          {!isFetching && results.length === 0 && (
            <div className="px-4 py-3 text-xs text-on-surface-variant">Keine Treffer in Hamburg — Name manuell eingeben.</div>
          )}

          {/* Inline count banner if any results are already in DB */}
          {duplicateCount > 0 && (
            <div className="px-4 py-2 bg-amber-50/70 border-b border-amber-100 flex items-center gap-2">
              <span className="material-symbols-outlined !text-[14px] text-amber-600">info</span>
              <span className="text-[10px] font-bold text-amber-800">
                {duplicateCount} Treffer bereits in Datenbank — gelb markiert
              </span>
            </div>
          )}

          {resultsAnnotated.map(({ hit: r, isDuplicate }) => (
            <div
              key={r.schulnummer}
              onClick={() => { onSelect(r); setIsOpen(false) }}
              className={`relative px-4 py-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                isDuplicate
                  ? 'bg-amber-50/60 border-amber-100 hover:bg-amber-100/70'
                  : 'border-outline-variant/30 hover:bg-primary/5'
              }`}
              title={isDuplicate ? 'Diese Schule ist bereits in der Datenbank vorhanden' : undefined}
            >
              {/* Left edge accent for duplicates */}
              {isDuplicate && (
                <span className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" aria-hidden="true" />
              )}

              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-bold truncate ${isDuplicate ? 'text-amber-900' : 'text-on-surface'}`}>{r.name}</p>
                    {isDuplicate && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500 text-white shadow-sm flex-shrink-0">
                        <span className="material-symbols-outlined !text-[11px]">check_circle</span>
                        Bereits vorhanden
                      </span>
                    )}
                  </div>
                  <p className={`text-[10px] mt-0.5 truncate ${isDuplicate ? 'text-amber-700/80' : 'text-on-surface-variant'}`}>
                    {r.schulform || '—'}{r.stadtteil ? ` · ${r.stadtteil}` : ''}
                  </p>
                  {(r.adresse || r.plz || r.stadt) && (
                    <p className={`text-[10px] mt-0.5 truncate flex items-center gap-1 ${isDuplicate ? 'text-amber-700/70' : 'text-on-surface-variant'}`}>
                      <span className="material-symbols-outlined !text-[11px]">location_on</span>
                      {[r.adresse, [r.plz, r.stadt].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                <span className={`text-[10px] font-black px-2 py-1 rounded-lg flex-shrink-0 ${
                  isDuplicate ? 'bg-amber-100 text-amber-800' : 'bg-primary/10 text-primary'
                }`}>
                  {r.schulnummer}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MapPanel({ lat, lon, adresse, plz, stadt, name }: {
  lat?: string | null; lon?: string | null; adresse?: string | null
  plz?: string | null; stadt?: string | null; name?: string
}) {
  const hasCoords = lat && lon
  const hasAddress = adresse && stadt
  const mapSrc = hasCoords
    ? `https://maps.google.com/maps?q=${lat},${lon}&z=15&output=embed`
    : hasAddress
      ? `https://maps.google.com/maps?q=${encodeURIComponent(`${adresse} ${plz || ''} ${stadt}`)}&z=14&output=embed`
      : null

  return (
    <div className="hidden md:flex flex-col w-72 bg-slate-950 p-6 text-white flex-shrink-0">
      <p className="text-[9px] font-black uppercase tracking-widest text-on-surface-variant mb-4">Standort</p>
      <div className="flex-1 bg-slate-900 rounded-2xl overflow-hidden relative group border border-white/5 flex items-center justify-center min-h-[200px]">
        {mapSrc ? (
          <iframe key={mapSrc} width="100%" height="100%" frameBorder="0"
            style={{ border: 0 }} referrerPolicy="no-referrer-when-downgrade" src={mapSrc} />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <span className="material-symbols-outlined text-on-surface text-4xl">location_off</span>
            <p className="text-[10px] text-on-surface text-center px-4">Adresse eingeben &amp; Koordinaten ermitteln</p>
          </div>
        )}
        {mapSrc && (
          <div className="absolute inset-0 bg-slate-950/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name || ''} ${adresse || ''} ${plz || ''} ${stadt || ''}`)}`}
              target="_blank" rel="noopener noreferrer"
              className="px-3 py-2 bg-surface-container-lowest text-on-surface rounded-xl font-bold text-[10px] uppercase tracking-widest flex items-center gap-1.5 hover:scale-105 transition-transform">
              <span className="material-symbols-outlined !text-base">open_in_new</span>
              Maps öffnen
            </a>
          </div>
        )}
      </div>
      {hasCoords && (
        <div className="mt-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold">Lat</span>
            <span className="font-mono text-[10px] text-on-surface-variant/50">{lat}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[9px] uppercase tracking-widest text-on-surface-variant font-bold">Lon</span>
            <span className="font-mono text-[10px] text-on-surface-variant/50">{lon}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main Modal ────────────────────────────────────────────────────────────────

export default function SchoolEditorModal({
  school, initialMode, onClose, onSave, onDelete, isSaving, isDeleting
}: Props) {
  const [mode, setMode] = useState<ModalMode>(initialMode ?? (school ? 'view' : 'add'))
  const [form, setForm] = useState<SchoolInput>(school ? schoolToForm(school) : EMPTY)
  const [deleteStage, setDeleteStage] = useState(0)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [geocodeError, setGeocodeError] = useState('')

  useEffect(() => {
    setMode(initialMode ?? (school ? 'view' : 'add'))
    setForm(school ? schoolToForm(school) : EMPTY)
    setDeleteStage(0)
    setGeocodeError('')
  }, [school, initialMode])

  const set = (key: keyof SchoolInput, value: string) =>
    setForm(p => ({ ...p, [key]: value || null }))

  const geocode = async () => {
    const q = [form.adresse, form.plz, form.stadt, form.bundesland].filter(Boolean).join(', ')
    if (!q) return
    setIsGeocoding(true)
    setGeocodeError('')
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`,
        { headers: { 'Accept-Language': 'de' } }
      )
      const data = await res.json()
      if (data[0]) {
        setForm(p => ({ ...p, lat: String(parseFloat(data[0].lat).toFixed(6)), lon: String(parseFloat(data[0].lon).toFixed(6)) }))
      } else {
        setGeocodeError('Keine Koordinaten gefunden')
      }
    } catch {
      setGeocodeError('Geocoding fehlgeschlagen')
    } finally {
      setIsGeocoding(false)
    }
  }

  const statusLower = (form.status || '').toLowerCase()
  const isActive  = statusLower === 'aktiv'
  const isStandby = statusLower === 'standby'
  const isView = mode === 'view'

  // View mode tab — keeps Stammdaten compact by default; Kennungen behind a tab
  const [viewTab, setViewTab] = useState<'stamm' | 'kennungen' | 'karten' | '3d'>('stamm')

  // ── Lieferanten-Kennungen state (SCD2 identifiers) ──────────────────────────
  const schoolKey = String(school?.id ?? school?.name?.toLowerCase().split(' ')[0] ?? '')
  // Scoped to this school — the whole-table pull takes ~10s in prod.
  const { data: identifiers = [], isLoading: identifiersLoading } = useIdentifiers(schoolKey || undefined)
  const schoolDisplayName = school?.name ?? ''

  const [identifierDialog, setIdentifierDialog] = useState<{
    open: boolean
    initial: SchoolSupplierIdentifier | null
    lieferantId: string | null
    hasExistingCurrent: boolean
    /** Die offenen Kennungen dieses Lieferanten — der Dialog erkennt daran ein
     *  zweites Konto und schlägt dann NICHT vor, die erste Kennung zu schließen. */
    currentCodes: string[]
  }>({ open: false, initial: null, lieferantId: null, hasExistingCurrent: false, currentCodes: [] })

  function handleAddIdentifier(lieferantId: string) {
    const today = new Date().toISOString().slice(0, 10)
    const offene = identifiers.filter(
      (i) =>
        i.schoolId === schoolKey &&
        i.lieferantId === lieferantId &&
        (i.validTo === null || i.validTo >= today),
    )
    setIdentifierDialog({
      open: true,
      initial: null,
      lieferantId,
      hasExistingCurrent: offene.length > 0,
      currentCodes: offene.map((i) => i.customerNumber),
    })
  }

  function handleEditIdentifier(identifier: SchoolSupplierIdentifier) {
    setIdentifierDialog({
      open: true,
      initial: identifier,
      lieferantId: identifier.lieferantId,
      hasExistingCurrent: false,
      currentCodes: [],
    })
  }

  function closeIdentifierDialog() {
    setIdentifierDialog({ open: false, initial: null, lieferantId: null, hasExistingCurrent: false, currentCodes: [] })
  }
  const canGeocode = !!(form.adresse || form.stadt)
  const showMaxStufe = form.kinderkategorie === 'GS' || form.kinderkategorie === 'WFS'

  // Firma list for dropdown (from FM mirror)
  const { data: firmaList = [] } = useFirma()
  const { hasRole } = usePermissions()
  const isItAdmin = hasRole('IT.Admin')

  // Firma lock — once a school has Firma set, only IT.Admin can change it
  const firmaAtLoad = school?.firma?.trim() || ''
  const firmaLocked = !!firmaAtLoad && !isItAdmin

  // Detect impending Aktiv → Inaktiv transition (only meaningful in edit mode)
  const willBecomeInaktiv =
    mode === 'edit' &&
    school?.status?.toLowerCase() !== 'inaktiv' &&
    statusLower === 'inaktiv'

  // Required fields — must be present before save can fire
  const missingName = !String(form.name ?? '').trim()
  const missingAbk  = !String(form.abkuerzung ?? '').trim()
  // Schultyp: one of the four values on EVERY save (also for an inactive school) — the server answers 400 SCHULTYP_FEHLT otherwise.
  const missingTyp  = schultypVon(form.kinderkategorie) === null

  // Uniqueness checks against existing DB (exclude self when editing).
  // The form's "Abkürzung" input writes to schuleLoginName on save (see
  // handleSave), and schuleLoginName is the per-school unique key.
  // The dim.Schools.abkuerzung column is the GROUP key (Hauptschule's
  // abbreviation) and is intentionally shared across all Standorte of a
  // cluster — it must NOT be used for uniqueness.
  const { data: allSchools = [] } = useSchulen()
  const normalizedAbk = String(form.abkuerzung ?? '').trim().toLowerCase()
  const normalizedSchulnummer = String(form.schulnummer ?? '').trim().toLowerCase()
  const abkTaken = !!normalizedAbk && allSchools.some(s =>
    s.id !== school?.id &&
    String(s.schuleLoginName ?? '').trim().toLowerCase() === normalizedAbk,
  )
  const schulnummerTaken = !!normalizedSchulnummer && allSchools.some(s =>
    s.id !== school?.id &&
    String(s.schulnummer ?? '').trim().toLowerCase() === normalizedSchulnummer,
  )

  const hasRequiredErrors = missingName || missingAbk || missingTyp || abkTaken || schulnummerTaken

  const applyHamburgHit = (r: HamburgLookupResult) => {
    setForm(p => ({
      ...p,
      name:            r.name            ?? p.name,
      schulnummer:     r.schulnummer     ?? p.schulnummer,
      adresse:         r.adresse         ?? p.adresse,
      plz:             r.plz             ?? p.plz,
      stadt:           r.stadt           ?? p.stadt,
      bundesland:      r.bundesland      ?? p.bundesland,
      lat:             r.lat             ?? p.lat,
      lon:             r.lon             ?? p.lon,
      kinderkategorie: r.kinderkategorie ?? p.kinderkategorie,
    }))
    notify.success(`${r.name} aus Hamburg-Portal geladen`)
  }

  // Schulnummer follows BASE-STANDORT format (e.g. 5358-0 for Hauptstandort, 5358-1 for the 2nd location).
  // If user enters just digits without a suffix, auto-append "-0".
  const normalizeSchulnummer = (raw: string | null | undefined): string | null => {
    if (!raw) return null
    const v = String(raw).trim()
    if (!v) return null
    return /^\d+$/.test(v) ? `${v}-0` : v
  }

  // Coerce a value that the backend expects as NVarChar — accepts string,
  // number (sometimes returned by the API), null, or undefined. Returns null
  // for empty/missing, string otherwise. Prevents the mssql driver's
  // "Validation failed for parameter '…'. Invalid string." rejection.
  const asNvarcharOrNull = (v: unknown): string | null => {
    if (v == null) return null
    const s = String(v).trim()
    return s === '' ? null : s
  }

  const handleSave = () => {
    onSave({
      ...form,
      schulnummer: normalizeSchulnummer(form.schulnummer),
      maxStufe: showMaxStufe ? (form.maxStufe || null) : '1',
      schuleLoginName: form.abkuerzung,
      // Backend mssql parameters are typed NVarChar — coerce numeric values
      // returned by the API (e.g. lat 52.544576 instead of "52.544576") so
      // round-tripping doesn't fail validation on the next save.
      lat: asNvarcharOrNull(form.lat),
      lon: asNvarcharOrNull(form.lon),
      plz: asNvarcharOrNull(form.plz),
      mandantId: asNvarcharOrNull(form.mandantId),
    })
  }

  // ── Delete footer button ─────────────────────────────────────────────────────

  const deleteBtn = school?.id && onDelete ? (
    isItAdmin ? (
      <Button
        variant="danger"
        size="sm"
        onClick={() => deleteStage < 1 ? setDeleteStage(1) : onDelete(school.id!)}
        disabled={isDeleting}
        loading={isDeleting}
      >
        {deleteStage === 1 ? 'Bestätigen?' : 'Löschen'}
      </Button>
    ) : (
      <div
        className="flex items-center gap-1.5 text-[10px] font-bold text-on-surface-variant"
        title="Löschen ist nur durch IT.Admin möglich. Setze die Schule stattdessen auf Inaktiv — sie verschwindet automatisch aus allen Ansichten und wird mit Zeitstempel protokolliert."
      >
        <span className="material-symbols-outlined !text-[14px]">lock</span>
        <span className="uppercase tracking-widest">Löschen: nur IT.Admin</span>
      </div>
    )
  ) : <div />

  // ── View Mode ────────────────────────────────────────────────────────────────

  const viewContent = (
    <div className="flex-1 flex overflow-hidden">
      {/* Left: Details */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        <div className="px-8 pt-8 border-b border-outline-variant/40">
          <div className="flex items-start justify-between gap-4 pb-5">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-on-surface leading-tight">{school?.name}</h2>
              {school?.abkuerzung && (
                <p className="text-[10px] font-black uppercase tracking-widest text-primary mt-1">{school.abkuerzung}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge tone={isActive ? 'success' : 'warn'}>{school?.status || 'Unbekannt'}</Badge>
              <Button variant="ghost" size="sm" icon="close" onClick={onClose} aria-label="Schließen" />
            </div>
          </div>
          {/* Tab strip — Stammdaten compact view stays default; Kennungen behind tab */}
          <div className="flex gap-1">
            {([
              { id: 'stamm', label: 'Stammdaten', icon: 'badge' },
              { id: 'kennungen', label: 'Kennungen', icon: 'key' },
              { id: 'karten', label: 'Karten', icon: 'badge' },
              { id: '3d', label: '3D', icon: 'view_in_ar' },
            ] as const).map((t) => {
              const isActiveTab = viewTab === t.id
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setViewTab(t.id)}
                  className={`px-3 py-2 -mb-px text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
                    isActiveTab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  <span className="material-symbols-outlined text-base">{t.icon}</span>
                  {t.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex-1 px-8 py-6 space-y-6">
          {viewTab === 'stamm' && (
            <>
              <section>
                <h4 className="text-[9px] font-black uppercase tracking-widest text-primary mb-3">Identifikation</h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <Field label="Schulnummer" value={school?.schulnummer} />
                  <Field label="PCA Login" value={school?.mandantId} />
                  <Field label="Firma" value={school?.firma} />
                  <Field label="Schultyp" value={schultypAnzeige(school?.kinderkategorie)} />
                  {(schultypVon(school?.kinderkategorie) === 'GS' || schultypVon(school?.kinderkategorie) === 'WFS') && (
                    <Field label="Max. Klassenstufe" value={school?.maxStufe} />
                  )}
                </div>
              </section>
              <section>
                <h4 className="text-[9px] font-black uppercase tracking-widest text-primary mb-3">Standort</h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <Field label="Adresse" value={school?.adresse} />
                  <Field label="PLZ" value={school?.plz} />
                  <Field label="Stadt" value={school?.stadt} />
                  <Field label="Bundesland" value={school?.bundesland} />
                </div>
              </section>
              <section>
                <h4 className="text-[9px] font-black uppercase tracking-widest text-primary mb-3">Management</h4>
                <Field label="Projektleiter" value={school?.projektleiter} />
              </section>
              {school?.inaktivSetAm && school.status?.toLowerCase() === 'inaktiv' && (
                <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-xl bg-surface-container border border-outline-variant text-[11px] text-on-surface">
                  <span className="material-symbols-outlined !text-[14px] text-on-surface-variant mt-0.5">history</span>
                  <div>
                    <span className="font-bold">Inaktiv gesetzt</span> am {new Date(school.inaktivSetAm).toLocaleString('de-DE')}
                    {school.inaktivSetBy && <span> · durch {school.inaktivSetBy}</span>}
                  </div>
                </div>
              )}
              {school?.syncedAt && (
                <p className="text-[10px] text-on-surface-variant/50 font-mono">Sync: {new Date(school.syncedAt).toLocaleString('de-DE')}</p>
              )}
            </>
          )}

          {viewTab === 'kennungen' && (
            <>
              {/* Lieferanten-Kennungen — SCD2 history per supplier */}
              <section>
                <h4 className="text-[9px] font-black uppercase tracking-widest text-primary mb-3">Lieferanten-Kennungen (SCD Type 2)</h4>
                {identifiersLoading ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-16 text-on-surface-variant">
                    <span className="material-symbols-outlined animate-spin text-3xl text-primary">progress_activity</span>
                    <p className="text-xs font-medium">Kennungen werden geladen…</p>
                  </div>
                ) : (
                  <SchoolIdentifiersPanel
                    schoolId={schoolKey}
                    identifiers={identifiers}
                    hideHeader
                    onAdd={handleAddIdentifier}
                    onEdit={handleEditIdentifier}
                  />
                )}
              </section>
            </>
          )}

          {viewTab === 'karten' && (
            school?.dimSchoolId != null ? (
              <SchoolCardsPanel schoolId={String(school.dimSchoolId)} schoolName={schoolDisplayName} />
            ) : (
              <p className="text-sm text-on-surface-variant">
                Diese Schule ist nicht im Stamm (dim.School) — Karten sind hier nicht möglich.
              </p>
            )
          )}

          {viewTab === '3d' && school && (
            <SchoolTourPanel school={school} />
          )}
        </div>

        <div className="px-8 py-5 border-t border-outline-variant/40 flex justify-between items-center">
          {deleteBtn}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose}>Schließen</Button>
            <Button variant="primary" onClick={() => setMode('edit')}>Bearbeiten</Button>
          </div>
        </div>
      </div>

      {/* Right: Map */}
      <MapPanel lat={school?.lat} lon={school?.lon} adresse={school?.adresse} plz={school?.plz} stadt={school?.stadt} name={school?.name} />
    </div>
  )

  // ── Edit / Add Mode ──────────────────────────────────────────────────────────

  const editContent = (
    <div className="flex-1 flex overflow-hidden">
      {/* Left: Form */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        <div className="px-8 pt-8 pb-6 border-b border-outline-variant/40 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-on-surface">{mode === 'add' ? 'Neue Schule' : 'Schule bearbeiten'}</h2>
          <div className="flex items-center gap-3">
            <StatusDropdown
              value={form.status || 'Aktiv'}
              onChange={v => set('status', v)}
              options={[
                { value: 'Aktiv', label: 'Aktiv' },
                { value: 'Standby', label: 'Standby' },
                { value: 'Inaktiv', label: 'Inaktiv' },
              ]}
              variant={isActive ? 'pill-active' : isStandby ? 'pill-active' : 'pill-inactive'}
            />
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-surface-container transition-colors text-on-surface-variant">
              <span className="material-symbols-outlined !text-xl">close</span>
            </button>
          </div>
        </div>

        <div className="flex-1 px-8 py-6 space-y-6">
          {/* Inaktiv transition warning */}
          {willBecomeInaktiv && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200">
              <span className="material-symbols-outlined !text-[18px] text-amber-600 mt-0.5">warning</span>
              <div className="flex-1">
                <p className="text-xs font-bold text-amber-900">Schule wird auf Inaktiv gesetzt</p>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  Die Schule verschwindet nach dem Speichern automatisch aus der Hierarchie, den Dashboards und
                  den Zuweisungen. Das System protokolliert den Zeitpunkt. Zum späteren Reaktivieren wieder auf Aktiv setzen.
                </p>
              </div>
            </div>
          )}
          {isStandby && mode === 'edit' && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-surface-container border border-outline-variant">
              <span className="material-symbols-outlined !text-[18px] text-on-surface-variant mt-0.5">pause_circle</span>
              <div className="flex-1">
                <p className="text-xs font-bold text-on-surface">Standby-Modus</p>
                <p className="text-[11px] text-on-surface mt-0.5">
                  Schule ist im System erfasst, wird aber nirgends angezeigt, bis sie auf Aktiv gesetzt wird.
                </p>
              </div>
            </div>
          )}

          {/* Identifikation */}
          <section>
            <h4 className="text-[9px] font-black uppercase tracking-widest text-primary mb-4">Identifikation</h4>
            <div className="grid grid-cols-2 gap-4">
              <SchoolNameAutocomplete
                value={form.name || ''}
                onChange={v => set('name', v)}
                onSelect={applyHamburgHit}
                required
                invalid={missingName}
                currentSchoolId={school?.id ?? null}
              />
              <div>
                <Input
                  label="Abkürzung"
                  value={form.abkuerzung || ''}
                  onChange={v => set('abkuerzung', v)}
                  required
                  invalid={missingAbk || abkTaken}
                />
                {abkTaken && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold text-error">
                    <span className="material-symbols-outlined !text-[12px]">error</span>
                    Abkürzung bereits vergeben
                  </p>
                )}
              </div>
              <SchulnummerInput
                value={form.schulnummer || ''}
                onChange={v => set('schulnummer', v)}
                invalid={schulnummerTaken}
                errorText={schulnummerTaken ? 'Schulnummer bereits vergeben' : undefined}
              />
              <Input label="PCA Login" value={form.mandantId || ''} onChange={v => set('mandantId', v)} />

              <div>
                {/* Shared Dropdown: real buttons (Tab/Enter), arrow keys, visible focus ring. The mandatory field must work without a mouse. */}
                <p className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-on-surface-variant mb-1.5">
                  Schultyp
                  <span className="text-error" aria-hidden="true">*</span>
                </p>
                <Dropdown
                  value={form.kinderkategorie || ''}
                  onChange={v => set('kinderkategorie', v)}
                  options={SCHULTYP_OPTIONEN}
                  placeholder="Schultyp wählen…"
                  ariaLabel={`Schultyp, Pflichtfeld${missingTyp ? '' : `: ${schultypAnzeige(form.kinderkategorie)}`}`}
                  invalid={missingTyp}
                  menuMinWidth={230}
                />
                {missingTyp && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-[10px] font-bold text-error">
                    <span className="material-symbols-outlined !text-[12px]">error</span>
                    Bitte einen Schultyp wählen
                  </p>
                )}
              </div>

              {showMaxStufe && (
                <Input
                  label="Max. Klassenstufe"
                  value={form.maxStufe || ''}
                  onChange={v => set('maxStufe', v)}
                  placeholder={form.kinderkategorie === 'GS' ? 'z.B. 4' : 'z.B. 13'}
                  colSpan={2}
                />
              )}
              <div className="col-span-2">
                <StatusDropdown
                  label="Firma"
                  value={form.firma || ''}
                  onChange={v => !firmaLocked && set('firma', v)}
                  options={firmaList.map(f => ({ value: f.firma || '', label: f.vollname || f.firma || '—' }))}
                  placeholder={firmaLocked ? (form.firma || '—') : 'Firma auswählen…'}
                />
                {firmaLocked && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-[10px] font-medium text-amber-700">
                    <span className="material-symbols-outlined !text-[12px]">lock</span>
                    Firma-Zuordnung gesperrt — Änderung nur durch IT.Admin
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Standort */}
          <section>
            <h4 className="text-[9px] font-black uppercase tracking-widest text-primary mb-4">Standort</h4>
            <div className="grid grid-cols-3 gap-4">
              <Input label="Adresse" value={form.adresse || ''} onChange={v => set('adresse', v)} colSpan={3} />
              <Input label="PLZ" value={form.plz || ''} onChange={v => set('plz', v)} />
              <Input label="Stadt" value={form.stadt || ''} onChange={v => set('stadt', v)} colSpan={2} />
              <Input label="Bundesland" value={form.bundesland || ''} onChange={v => set('bundesland', v)} colSpan={3} />
            </div>

            {/* Geocode row */}
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={geocode}
                disabled={!canGeocode || isGeocoding}
                className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className={`material-symbols-outlined !text-base ${isGeocoding ? 'animate-spin' : ''}`}>
                  {isGeocoding ? 'progress_activity' : 'my_location'}
                </span>
                {isGeocoding ? 'Ermittle…' : 'Koordinaten ermitteln'}
              </button>

              {(form.lat && form.lon) && (
                <span className="font-mono text-[10px] text-on-surface-variant">
                  {form.lat}, {form.lon}
                </span>
              )}
              {geocodeError && (
                <span className="text-[10px] text-error font-bold">{geocodeError}</span>
              )}
            </div>
          </section>

          {/* Management */}
          <section>
            <h4 className="text-[9px] font-black uppercase tracking-widest text-primary mb-4">Management</h4>
            <Input label="Projektleiter" value={form.projektleiter || ''} onChange={v => set('projektleiter', v)} colSpan={2} />
          </section>
        </div>

        <div className="px-8 py-5 border-t border-outline-variant/40 flex justify-between items-center">
          {deleteBtn}
          <div className="flex gap-3">
            {mode === 'edit' && (
              <Button variant="ghost" onClick={() => setMode('view')}>Abbrechen</Button>
            )}
            {mode === 'add' && (
              <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
            )}
            <Button variant="primary" onClick={handleSave} disabled={isSaving || hasRequiredErrors} loading={isSaving}>
              Speichern
            </Button>
          </div>
        </div>
      </div>

      {/* Right: Map (live updates as form changes) */}
      <MapPanel lat={form.lat} lon={form.lon} adresse={form.adresse} plz={form.plz} stadt={form.stadt} name={form.name} />
    </div>
  )

  return (
    <>
      {createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-slate-950/60 animate-[fade-in_0.2s_ease-out]"
          onClick={e => { if (e.target === e.currentTarget) onClose() }}
        >
          <div className="bg-surface-container-lowest rounded-[2.5rem] w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col animate-[fade-in-up_0.3s_ease-out]">
            {isView ? viewContent : editContent}
          </div>
        </div>,
        document.body,
      )}

      {/* Lieferanten-Kennung add/edit dialog (mounted via portal) */}
      <IdentifierEditDialog
        open={identifierDialog.open}
        schoolId={schoolKey}
        schoolName={schoolDisplayName}
        initial={identifierDialog.initial}
        lieferantId={identifierDialog.lieferantId}
        hasExistingCurrent={identifierDialog.hasExistingCurrent}
        currentCodes={identifierDialog.currentCodes}
        onClose={closeIdentifierDialog}
      />
    </>
  )
}

function schoolToForm(school: School): SchoolInput {
  return {
    name: school.name, schulnummer: school.schulnummer, firma: school.firma,
    bundesland: school.bundesland, stadt: school.stadt, plz: school.plz,
    adresse: school.adresse, lat: school.lat, lon: school.lon,
    projektleiter: school.projektleiter,
    // Old names (GBS, KiTa, Hort, Krippe, Sonstige) open as their mapped value; anything unknown opens empty (= must be chosen).
    kinderkategorie: schultypVon(school.kinderkategorie),
    // The "Abkürzung" input represents the per-school login name. Read from
    // schuleLoginName (the unique per-school column); fall back to abkuerzung
    // only when schuleLoginName is unset on legacy rows.
    abkuerzung: school.schuleLoginName ?? school.abkuerzung,
    maxStufe: school.maxStufe, mandantId: school.mandantId,
    schuleLoginName: school.schuleLoginName, status: school.status,
  }
}
